import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTemplates,
  getTemplate,
  createTemplate,
  deleteTemplate,
  updateTemplate,
  bulkDeleteTemplates,
  deleteAllTemplates,
  updateTemplateVisibility,
  archiveTemplate,
  unarchiveTemplate,
} from '@/services/templateApi';
import { useAuth } from '@/contexts/AuthContext';
import type { CreateTemplateRequest } from '@/types/api';

/**
 * Hook to fetch all templates.
 * Query key includes user.id to isolate caches when users switch accounts,
 * and includeArchived so the active and archived lists are cached separately.
 * Query is disabled when unauthenticated (no user.id).
 *
 * @param includeArchived When true, fetch archived templates too (D12).
 */
export function useTemplates(includeArchived = false) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['templates', user?.id, { includeArchived }],
    queryFn: () => getTemplates(includeArchived),
    enabled: !!user?.id,
  });
}

/**
 * Hook to fetch a single template
 */
export function useTemplate(templateId: string | undefined) {
  return useQuery({
    queryKey: ['templates', templateId],
    queryFn: () => getTemplate(templateId!),
    enabled: !!templateId,
  });
}

/**
 * Hook to create a template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    // Wrap createTemplate so the optional channelId isn't confused with
    // React Query's internal MutationFunctionContext second argument.
    // The cast is needed because the api.ts and templateApi.ts types diverge
    // (pre-existing mismatch — templateApi.ts is the authoritative type).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (request: CreateTemplateRequest) => createTemplate(request as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

/**
 * Hook to update a template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateTemplateRequest> }) =>
      updateTemplate(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templates', variables.id] });
    },
  });
}

/**
 * Hook to delete a template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

/**
 * Hook to bulk delete templates by ID list
 */
export function useBulkDeleteTemplates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateIds: string[]) => bulkDeleteTemplates(templateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

/**
 * Hook to delete all templates
 */
export function useDeleteAllTemplates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteAllTemplates(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

/**
 * Hook to update template visibility (Security P2-1).
 * Sends PATCH /templates/{id}/visibility.
 */
export function useUpdateTemplateVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: 'private' | 'public' }) =>
      updateTemplateVisibility(id, visibility),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templates', variables.id] });
    },
  });
}

/**
 * Hook to archive a template (D12). Sends PATCH /templates/{id}/archive.
 */
export function useArchiveTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => archiveTemplate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templates', id] });
    },
  });
}

/**
 * Hook to un-archive a template (D12). Sends PATCH /templates/{id}/unarchive.
 * The mutation rejects with a 409 when the active-template cap is reached;
 * callers should surface that to the user.
 */
export function useUnarchiveTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => unarchiveTemplate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templates', id] });
    },
  });
}
